import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx-js-style';
import './App.css';

// Compara estrictamente lo pedido vs lo enviado (sin tolerancia de 0.1).
// Épsilon 1e-9 solo para artefactos de punto flotante (cant × factor).
export const clasificarCoincidencia = (pedido, enviado) => {
  const diff = pedido - enviado;
  if (Math.abs(diff) < 1e-9) return { texto: "Exacto", etiqueta: "exacto" };
  return diff > 0
    ? { texto: "Menos enviado", etiqueta: "menos" }
    : { texto: "Más enviado", etiqueta: "mas" };
};

// Los refrescos llegan con prefijo de pasillo (B1, Ñ1...) o solos: "REFRESCOS".
export const esRefresco = (categoria) =>
  String(categoria || '').toUpperCase().includes('REFRESCOS');

// Convierte piezas a "cajas / piezas": 27 pzas con factor 12 → "2 / 3".
// En negativos el signo va en ambos componentes: -15 → "-1 / -3".
// Math.round absorbe artefactos de punto flotante (cant × factor).
export const formatoCajasPiezas = (piezas, factor) => {
  const f = factor > 0 ? factor : 1;
  const total = Math.round(Math.abs(piezas));
  const signo = piezas < 0 ? '-' : '';
  const cajas = Math.trunc(total / f);
  const sueltas = total % f;
  return `${signo}${cajas} / ${sueltas ? signo + sueltas : sueltas}`;
};

function App() {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Estado para controlar el mes/año visible en el calendario
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());

  // Estados de la aplicación
  const [selectedPedido, setSelectedPedido] = useState(null); // { name, workbook }
  const [localTraspasos, setLocalTraspasos] = useState([]); // File objects
  const [localExistencias, setLocalExistencias] = useState(null); // File object
  const [localObservaciones, setLocalObservaciones] = useState(null); // File object
  const [currentLogId, setCurrentLogId] = useState(null);

  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [stats, setStats] = useState({ total: 0, completo: 0, parcial: 0, sinStock: 0, conStock: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [sucursal, setSucursal] = useState("");

  // Estados de Vista Previa
  const [previewData, setPreviewData] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  const API_URL = "/api/pedidos-upload";

  // Estado para feedback
  const [subidasHoy, setSubidasHoy] = useState(null);
  const [historialComparaciones, setHistorialComparaciones] = useState(null);

  const fetchSubidasHoy = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/subidas-hoy/`);
      setSubidasHoy(res.data);
    } catch (err) {
      console.error("Error cargando subidas:", err);
    }
  }, []);

  const fetchHistorialComparaciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/historial-comparaciones/`);
      setHistorialComparaciones(res.data);
    } catch (err) {
      console.error("Error cargando historial:", err);
    }
  }, []);

  useEffect(() => {
    fetchDates();
    fetchSubidasHoy();
    fetchHistorialComparaciones();
    const feedbackInterval = setInterval(() => {
      fetchSubidasHoy();
      fetchHistorialComparaciones();
    }, 30000);
    return () => clearInterval(feedbackInterval);
  }, [fetchSubidasHoy, fetchHistorialComparaciones]);

  const fetchDates = async () => {
    setLoadingDates(true);
    try {
      const response = await axios.get(`${API_URL}/dates/`);
      setDates(response.data);

      // Si hay fechas, centrar el calendario en la fecha más reciente
      if (response.data && response.data.length > 0) {
        // Ordenamos las fechas de mayor a menor para obtener la más reciente
        const sortedDates = [...response.data].sort((a, b) => new Date(b) - new Date(a));
        const [year, month] = sortedDates[0].split('-');
        setCalendarViewDate(new Date(year, month - 1, 1));
      }
    } catch (error) {
      console.error("Error fetching dates:", error);
      Swal.fire('Error', 'No se pudieron cargar las fechas.', 'error');
    } finally {
      setLoadingDates(false);
    }
  };

  const handleDateClick = async (dateString) => {
    setSelectedDate(dateString);
    setLoadingFiles(true);
    setFiles([]);
    setSelectedPedido(null);
    setResults(null);
    setPreviewData(null);
    try {
      const response = await axios.get(`${API_URL}/files/${dateString}`);
      setFiles(response.data);
    } catch (error) {
      console.error("Error fetching files:", error);
      Swal.fire('Error', 'No se pudieron cargar los archivos.', 'error');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleBackToCalendar = () => {
    setSelectedDate(null);
    setFiles([]);
    setSelectedPedido(null);
  };

  // --- LÓGICA DE PARSEO ---
  const esUnidadSimple = (unidad) => {
    if (!unidad) return false;
    const u = unidad.toUpperCase().trim();
    return u.includes("PZ") || u.includes("PIEZA") || u.includes("PZA") || u.includes("SOBRE") || u.includes("KILO") || u.includes("KG");
  };

  const findHeader = (sheet, cols) => {
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
    for (let i = 0; i < rows.length; i++) {
      const rowStr = (rows[i] || []).map(x => String(x).toUpperCase());
      if (cols.every(c => rowStr.some(r => r.includes(c.toUpperCase())))) return { index: i, row: rowStr };
    }
    return null;
  };

  const parsearPedido = (wb, filename) => {
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
    let detectedSuc = "";

    // 1. Intentar detectar desde celdas
    for (let i = 0; i < 5 && i < rows.length; i++) {
      const row = rows[i]; if (!row) continue;
      for (let cell of row) {
        if (String(cell).toUpperCase().includes("PEDIDO SUC")) {
          detectedSuc = String(cell).trim();
          break;
        }
      }
      if (detectedSuc) break;
    }

    // 2. Fallback: Detectar desde el nombre del archivo (ej: PD 060...)
    if (!detectedSuc && filename) {
      const match = filename.match(/PD\s+(\d+)/i) || filename.match(/SUC\s+(\d+)/i);
      if (match) detectedSuc = `PEDIDO SUC ${match[1]}`;
    }

    if (!detectedSuc) detectedSuc = "Desconocida";

    setSucursal(detectedSuc);

    const headerInfo = findHeader(sheet, ['CLAVE', 'CANTIDAD']);
    if (!headerInfo) throw new Error("Faltan columnas Clave/Cantidad en el Pedido.");
    const header = headerInfo.row;
    const idxClave = header.findIndex(h => h.includes("CLAVE"));
    const idxCant = header.findIndex(h => h.includes("CANTIDAD") && h.includes("ENVIAR"));
    const idxFactor = header.findIndex(h => h.includes("FACTOR"));
    const idxCat = header.findIndex(h => h.includes("CATEGOR"));
    const idxDesc = header.findIndex(h => h.includes("DESCRIPCI"));
    const idxUnidad = (idxCant !== -1) ? idxCant + 1 : -1;

    const pedidosArr = [];
    for (let i = headerInfo.index + 1; i < rows.length; i++) {
      const row = rows[i]; if (!row || !row[idxClave]) continue;
      const ean = String(row[idxClave]).trim();
      const cant = Number(row[idxCant]);
      const fac = Number(row[idxFactor]);
      const unidad = (idxUnidad !== -1 && row[idxUnidad]) ? String(row[idxUnidad]) : "PIEZA";
      if (isNaN(cant)) continue;

      let cantFinal = cant;
      if (!esUnidadSimple(unidad)) {
        cantFinal = cant * ((fac && fac > 0) ? fac : 1);
      }
      pedidosArr.push({ ean, descripcion: idxDesc > -1 ? row[idxDesc] : '', categoria: idxCat > -1 ? row[idxCat] : '', factor: fac || 1, pedido: cantFinal });
    }
    return { pedidosArr, detectedSuc };
  };

  const parsearTraspasosMultiples = async (fileList, pedidos) => {
    const factorMap = {}; pedidos.forEach(p => factorMap[p.ean] = p.factor);
    const mapaGlobal = {};

    for (const file of fileList) {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const headerInfo = findHeader(sheet, ['DUN', 'CANTIDAD']);
      if (!headerInfo) continue;

      const header = headerInfo.row;
      const idxEan = header.findIndex(h => h.includes("DUN") || h.includes("EAN"));
      const idxCant = header.findIndex(h => h === "CANTIDAD");
      let idxEmpaque = header.findIndex(h => h.includes("EMPAQUE") && h.includes("TRASLADO"));
      if (idxEmpaque === -1) idxEmpaque = header.findIndex(h => h.includes("EMPAQUE"));

      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      for (let i = headerInfo.index + 1; i < rows.length; i++) {
        const row = rows[i]; if (!row || !row[idxEan]) continue;
        const ean = String(row[idxEan]).trim();
        const cant = Number(row[idxCant]);
        if (isNaN(cant)) continue;

        const unidadTxt = row[idxEmpaque] ? String(row[idxEmpaque]) : "PIEZA";
        const factor = factorMap[ean] || 1;
        let cantFinal = esUnidadSimple(unidadTxt) ? cant : cant * factor;

        mapaGlobal[ean] = (mapaGlobal[ean] || 0) + cantFinal;
      }
    }
    return mapaGlobal;
  };

  const parsearExistencias = async (file) => {
    const data = await file.arrayBuffer();
    const wb = XLSX.read(data, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    let startRow = 0; let idxEan = 0;
    for (let i = 0; i < rows.length; i++) {
      const str = (rows[i] || []).join(' ').toUpperCase();
      if (str.includes("DUN") || str.includes("EAN") || str.includes("CLAVE")) {
        startRow = i + 1; idxEan = rows[i].findIndex(c => String(c).toUpperCase().match(/DUN|EAN|CLAVE/)); break;
      }
    }
    const mapa = {}; const IDX_D = 3, IDX_G = 6, IDX_I = 8;
    for (let i = startRow; i < rows.length; i++) {
      const row = rows[i]; if (!row) continue;
      const eanVal = row[idxEan]; if (!eanVal) continue;
      const ean = String(eanVal).trim();
      const total = (Number(row[IDX_G]) || 0) * (Number(row[IDX_D]) || 0) + (Number(row[IDX_I]) || 0);
      mapa[ean] = (mapa[ean] || 0) + total;
    }
    return mapa;
  };

  // --- INTERACCIONES ---
  const handleSelectFile = async (filename) => {
    if (!selectedDate) return;
    setProcessing(true);
    setResults(null);
    try {
      const response = await axios.get(`${API_URL}/download/${selectedDate}/${filename}`, { responseType: 'arraybuffer' });
      const workbook = XLSX.read(new Uint8Array(response.data), { type: 'array' });
      setSelectedPedido({ name: filename, workbook });
    } catch (error) {
      Swal.fire('Error', 'No se pudo cargar el archivo del servidor.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handlePreview = async (filename) => {
    if (!selectedDate) return;
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${API_URL}/download/${selectedDate}/${filename}`, { responseType: 'arraybuffer' });
      const data = new Uint8Array(response.data);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

      setPreviewTitle(filename);
      setPreviewData(jsonData);
    } catch (error) {
      Swal.fire('Error', 'No se pudo generar la vista previa.', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const runComparison = async () => {
    if (!selectedPedido || !localTraspasos.length || !localExistencias) {
      Swal.fire('Atención', 'Faltan archivos para procesar.', 'warning');
      return;
    }
    setProcessing(true);
    try {
      const { pedidosArr, detectedSuc } = parsearPedido(selectedPedido.workbook, selectedPedido.name);
      const traspasosMapa = await parsearTraspasosMultiples(localTraspasos, pedidosArr);
      const existenciasMapa = await parsearExistencias(localExistencias);

      let s = { total: pedidosArr.length, completo: 0, parcial: 0, sinStock: 0, conStock: 0 };
      const finalRes = pedidosArr.map(p => {
        const enviado = traspasosMapa[p.ean] || 0;
        const existencia = existenciasMapa[p.ean] || 0;
        let estado = "", etiqueta = "";

        if (enviado >= p.pedido - 0.1) {
          estado = "Completo"; etiqueta = "completo"; s.completo++;
        } else if (enviado > 0) {
          estado = "Parcial"; etiqueta = "parcial"; s.parcial++;
        } else {
          if (existencia < p.pedido) {
            estado = "No enviado (Sin stock)"; etiqueta = "sin-existencia"; s.sinStock++;
          } else {
            estado = "No enviado (Con stock)"; etiqueta = "con-existencia"; s.conStock++;
          }
        }
        return { ...p, enviado, existencia, estado, etiqueta, diff: p.pedido - enviado, coincidencia: clasificarCoincidencia(p.pedido, enviado) };
      });

      setStats(s);
      setResults(finalRes);

      // Enviar log al backend para reporte diario y guardar ID
      try {
        const resp = await axios.post(`${API_URL}/log-comparacion/`, {
          sucursal: detectedSuc,
          stats: s
        });
        if(resp.data && resp.data.id) {
          setCurrentLogId(resp.data.id);
        }
      } catch (logErr) {
        console.error("Error al guardar log:", logErr);
      }

    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setProcessing(false);
      // Refrescar feedback después de comparar
      fetchHistorialComparaciones();
    }
  };

  const handleUploadObservacion = async () => {
    if (!localObservaciones || !currentLogId) return;
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("file", localObservaciones);
      formData.append("sucursal", sucursal);
      formData.append("log_id", currentLogId);

      await axios.post(`${API_URL}/upload_observacion/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire("Éxito", "Tus observaciones se han guardado y enlazado a este reporte.", "success");
      setLocalObservaciones(null); // Limpiamos para que se oculte el botón
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Falló la carga del archivo de observaciones.", "error");
    } finally {
      setProcessing(false);
    }
  };

  // Exporta productos con estado "No enviado (Con stock)"
  // y además los refrescos con diferencia (menos o más enviado).
  const exportComparativo = () => {
    if (!results) return;
    const filtrados = results.filter(r => r.etiqueta === 'con-existencia' || (esRefresco(r.categoria) && r.coincidencia.etiqueta !== 'exacto'));
    if (!filtrados.length) { Swal.fire('Info', 'No hay incidencias para exportar.', 'info'); return; }

    const header = ["Sucursal", "Categoría", "Clave", "Descripción", "Factor", "Pedido (CJ / PZ)", "Enviado (CJ / PZ)", "Existencia (CJ / PZ)", "Diferencia (CJ / PZ)", "Coincidencia"];

    // Cantidades como "cajas / piezas" en una sola celda: 27 pzas con factor 12 → "2 / 3".
    const dataRows = filtrados.map(r => [
      sucursal,
      r.categoria,
      r.ean,
      r.descripcion,
      r.factor,
      formatoCajasPiezas(r.pedido, r.factor),
      formatoCajasPiezas(r.enviado, r.factor),
      formatoCajasPiezas(r.existencia, r.factor),
      formatoCajasPiezas(r.diff, r.factor),
      r.coincidencia.texto
    ]);

    // Combinar header y filas
    const aoa = [header, ...dataRows];

    // Crear hoja de cálculo
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Aplicar estilos morados a los encabezados (Fila 1)
    const range = XLSX.utils.decode_range(ws['!ref']);
    const purpleHeaderStyle = {
      fill: { fgColor: { rgb: "6B21A8" } }, // Morado
      font: { color: { rgb: "FFFFFF" }, bold: true },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Aplicar estilo a cada celda del encabezado (A1 hasta la última columna de la fila 1)
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = purpleHeaderStyle;
    }

    // Pintar toda la fila de refrescos (B1/Ñ1 REFRESCOS, etc.) sin tocar columnas ni formato
    const refrescoFill = { fill: { fgColor: { rgb: "FFF2CC" } } }; // Ámbar claro
    filtrados.forEach((r, i) => {
      if (!esRefresco(r.categoria)) return;
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + (i + 2);
        if (!ws[address]) continue;
        ws[address].s = refrescoFill;
      }
    });

    // Ajustar anchos de columna básicos
    ws['!cols'] = [
      { wch: 15 }, // Sucursal
      { wch: 20 }, // Categoría
      { wch: 15 }, // Clave
      { wch: 40 }, // Descripción
      { wch: 10 }, // Factor
      { wch: 14 }, // Pedido (CJ / PZ)
      { wch: 14 }, // Enviado (CJ / PZ)
      { wch: 14 }, // Existencia (CJ / PZ)
      { wch: 14 }, // Diferencia (CJ / PZ)
      { wch: 15 }  // Coincidencia
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comparativo");
    XLSX.writeFile(wb, `Comparativo_${sucursal.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
  };

  const clearProject = () => {
    setSelectedDate(null);
    setFiles([]);
    setSelectedPedido(null);
    setLocalTraspasos([]);
    setLocalExistencias(null);
    setLocalObservaciones(null);
    setCurrentLogId(null);
    setResults(null);
    setSucursal("");
  };

  // --- LÓGICA DEL CALENDARIO ---
  const formatBackendDate = (y, m, d) => {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  const renderCalendarDays = () => {
    const year = calendarViewDate.getFullYear();
    const month = calendarViewDate.getMonth();

    // El día 1 del mes (0 = Domingo, 1 = Lunes...)
    let firstDay = new Date(year, month, 1).getDay();
    // Ajustamos para que Lunes sea el primer día de la semana (0) y Domingo el último (6)
    firstDay = firstDay === 0 ? 6 : firstDay - 1;

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const availableDatesSet = new Set(dates);

    const days = [];

    // Espacios vacíos al inicio del mes
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Días del mes
    for (let d = 1; d <= daysInMonth; d++) {
      const dateString = formatBackendDate(year, month, d);
      const isAvailable = availableDatesSet.has(dateString);

      days.push(
        <div
          key={d}
          className={`calendar-day ${isAvailable ? 'active' : 'disabled'}`}
          onClick={() => isAvailable && handleDateClick(dateString)}
          title={isAvailable ? "Haz clic para ver pedidos" : "Sin pedidos"}
        >
          <span className="day-number">{d}</span>
          {isAvailable && <span className="day-indicator">📦</span>}
        </div>
      );
    }
    return days;
  };

  const nextMonth = () => {
    setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1));
  };

  const formatDateLabel = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const formatted = date.toLocaleDateString('es-ES', options);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const filteredResults = results ? results.filter(r =>
    r.ean.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  return (
    <div className="app-container">
      <main className="main-content">

        <header className="header">
          <div className="header-content">
            <h1>📦 Comparador de Pedidos</h1>
            <p>
              {!selectedDate
                ? 'Selecciona una fecha en el calendario para comenzar'
                : selectedPedido
                  ? `Analizando: ${selectedPedido.name}`
                  : `Día seleccionado: ${formatDateLabel(selectedDate)}`}
            </p>
          </div>
          {sucursal && <div className="sucursal-badge">📍 {sucursal}</div>}
        </header>

        {/* PASO 1: CALENDARIO GRANDE */}
        {!selectedDate && (
          <div className="calendar-wrapper">
            {loadingDates ? (
              <div className="loading-state">Cargando calendario...</div>
            ) : (
              <div className="calendar-card">
                <div className="calendar-header">
                  <button onClick={prevMonth} className="btn-cal-nav">&#10094;</button>
                  <h2>{monthNames[calendarViewDate.getMonth()]} {calendarViewDate.getFullYear()}</h2>
                  <button onClick={nextMonth} className="btn-cal-nav">&#10095;</button>
                </div>
                <div className="calendar-weekdays">
                  <div>Lun</div><div>Mar</div><div>Mié</div><div>Jue</div><div>Vie</div><div>Sáb</div><div>Dom</div>
                </div>
                <div className="calendar-grid">
                  {renderCalendarDays()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ PANEL FEEDBACK: Sucursales que han subido hoy ═══ */}
        {!selectedDate && subidasHoy && (
          <div className="feedback-visor-panel">
            <div className="feedback-visor-header">
              <h3>📤 Sucursales que han subido pedidos hoy</h3>
              <div className="feedback-visor-counters">
                <span className="fv-badge subidas">{subidasHoy.total_sucursales} subieron</span>
                {historialComparaciones && (
                  <span className="fv-badge comparadas">{historialComparaciones.sucursales_comparadas.length} comparadas</span>
                )}
              </div>
            </div>

            {subidasHoy.sucursales_unicas.length > 0 ? (
              <div className="fv-sucursales-grid">
                {subidasHoy.sucursales_unicas.map((suc, i) => {
                  const yaComparada = historialComparaciones?.sucursales_comparadas?.some(
                    sc => sc.toUpperCase().includes(suc)
                  );
                  return (
                    <div key={i} className={`fv-sucursal-card ${yaComparada ? 'comparada' : 'pendiente'}`}>
                      <span className="fv-suc-nombre">SUC {suc}</span>
                      <span className={`fv-suc-estado ${yaComparada ? 'estado-ok' : 'estado-pendiente'}`}>
                        {yaComparada ? '✅ Comparada' : '⏳ Pendiente'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="fv-no-data">Aún no se han subido pedidos hoy.</p>
            )}

            {/* Detalle de subidas del día */}
            {subidasHoy.detalle.length > 0 && (
              <div className="fv-detalle-section">
                <h4>Registro de subidas del día ({subidasHoy.total_subidas})</h4>
                <div className="fv-detalle-list">
                  {subidasHoy.detalle.map((item, i) => (
                    <div key={i} className={`fv-detalle-row ${item.es_duplicado ? 'dup' : ''}`}>
                      <span className="fv-d-hora">{item.hora}</span>
                      <span className="fv-d-suc">SUC {item.sucursal}</span>
                      <span className="fv-d-file" title={item.filename}>{item.filename}</span>
                      {item.es_duplicado && <span className="fv-d-tag">↺ Actualización</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ PANEL FEEDBACK: Historial de comparaciones ═══ */}
        {!selectedDate && historialComparaciones && historialComparaciones.detalle.length > 0 && (
          <div className="feedback-visor-panel comparaciones-panel">
            <div className="feedback-visor-header">
              <h3>🔍 Comparaciones realizadas hoy</h3>
              <span className="fv-badge comparadas">{historialComparaciones.total_comparaciones} total</span>
            </div>
            <div className="fv-comp-list">
              {historialComparaciones.detalle.map((comp, i) => {
                const total = comp.stats.total || 1;
                const pctOk = Math.round(((comp.stats.completo || 0) / total) * 100);
                return (
                  <div key={i} className="fv-comp-card">
                    <div className="fv-comp-top">
                      <span className="fv-comp-suc">{comp.sucursal}</span>
                      <span className="fv-comp-hora">{comp.hora}</span>
                    </div>
                    <div className="fv-comp-bar-container">
                      <div className="fv-comp-bar" style={{width: `${pctOk}%`}}></div>
                    </div>
                    <div className="fv-comp-stats">
                      <span className="fv-cs ok">✅ {comp.stats.completo}</span>
                      <span className="fv-cs warn">⚠️ {comp.stats.parcial}</span>
                      <span className="fv-cs danger">❌ {comp.stats.sinStock}</span>
                      <span className="fv-cs info">📦 {comp.stats.conStock}</span>
                      {comp.tiene_observaciones && <span className="fv-cs obs">📝</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PASO 2: SELECCIÓN DE ARCHIVOS (PEDIDOS) */}
        {selectedDate && !selectedPedido && (
          <div className="files-view">
            <div className="config-bar mb-20">
              <button className="btn-back" onClick={handleBackToCalendar}>← Volver al Calendario</button>
              <div className="pedido-info"><strong>{files.length}</strong> pedidos encontrados</div>
            </div>

            <div className="file-grid">
              {loadingFiles ? <div className="loading-state">Cargando pedidos...</div> : files.length > 0 ? files.map((f, i) => (
                <div key={i} className="file-card">
                  <div className="file-icon">📊</div>
                  <div className="file-name" title={f}>{f}</div>
                  <div className="actions">
                    <button className="action-btn preview-btn" onClick={() => handlePreview(f)}>Ver</button>
                    <button className="action-btn select-btn" onClick={() => handleSelectFile(f)}>Seleccionar</button>
                  </div>
                </div>
              )) : <div className="no-selection">No hay archivos para esta fecha.</div>}
            </div>
          </div>
        )}

        {/* PASO 3: DASHBOARD DE COMPARACIÓN */}
        {selectedDate && selectedPedido && (
          <div className="comparator-dashboard">
            <div className="config-bar">
              <button className="btn-back" onClick={() => setSelectedPedido(null)}>← Cambiar Pedido</button>
              <div className="pedido-info"><strong>Pedido actual:</strong> {selectedPedido.name}</div>
            </div>

            <div className="upload-section">
              <div className="upload-card">
                <h3>🚚 Traspasos (Excel)</h3>
                <label className={`drop-zone ${localTraspasos.length ? 'active' : ''}`}>
                  <input type="file" multiple onChange={e => setLocalTraspasos(Array.from(e.target.files))} />
                  {localTraspasos.length ? `${localTraspasos.length} archivos subidos` : 'Haz clic o arrastra aquí'}
                </label>
              </div>
              <div className="upload-card">
                <h3>🏠 Existencias (Excel)</h3>
                <label className={`drop-zone ${localExistencias ? 'active' : ''}`}>
                  <input type="file" onChange={e => setLocalExistencias(e.target.files[0])} />
                  {localExistencias ? localExistencias.name : 'Haz clic o arrastra aquí'}
                </label>
              </div>
            </div>

            <div className="action-row">
              <button className="btn btn-primary" onClick={runComparison} disabled={processing}>
                {processing ? 'Procesando...' : '🚀 Comparar Datos'}
              </button>
              {results && <button className="btn btn-success" onClick={exportComparativo}>📥 Exportar Comparativo</button>}
              <button className="btn btn-secondary" onClick={clearProject}>♻️ Empezar de Nuevo</button>
            </div>

            {results && (
              <>
                <div className="stats-grid">
                  <div className="stat-card"><span className="stat-label">Total Items</span><span className="stat-val">{stats.total}</span></div>
                  <div className="stat-card success"><span className="stat-label">Completos</span><span className="stat-val">{stats.completo}</span></div>
                  <div className="stat-card warning"><span className="stat-label">Parciales</span><span className="stat-val">{stats.parcial}</span></div>
                  <div className="stat-card danger"><span className="stat-label">Sin Stock</span><span className="stat-val">{stats.sinStock}</span></div>
                  <div className="stat-card info"><span className="stat-label">Con Stock</span><span className="stat-val">{stats.conStock}</span></div>
                </div>

                <div className="observaciones-dashboard" style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '20px', border: '1px dashed #cbd5e1' }}>
                  <div style={{flex: 1}}>
                    <h3 style={{marginTop: 0, color: '#334155'}}>📝 Subir Observaciones</h3>
                    <p style={{margin: 0, fontSize: '0.9em', color: '#64748b'}}>Genera primero el Excel del Comparativo, coloca tus observaciones y súbelo aquí para anexarlo al PDF.</p>
                  </div>
                  <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <label className={`drop-zone ${localObservaciones ? 'active' : ''}`} style={{minHeight: '40px', padding: '10px 20px', margin: 0, flex: 1, whiteSpace: 'nowrap'}}>
                      <input type="file" onChange={e => setLocalObservaciones(e.target.files[0])} />
                      {localObservaciones ? localObservaciones.name : 'Subir archivo...'}
                    </label>
                    {localObservaciones && (
                      <button className="btn btn-primary" onClick={handleUploadObservacion} disabled={processing}>
                        {processing ? 'Subiendo...' : '💾 Guardar Anexo'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="results-container">
                  <div className="results-header">
                    <h3>Resultados de la Comparación</h3>
                    <input type="text" placeholder="Buscar por clave o descripción..." className="search-box" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  </div>
                  <div className="table-wrapper">
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Categoría</th><th>Clave</th><th>Descripción</th><th>Factor</th><th>Pedido (CJ / PZ)</th><th>Enviado (CJ / PZ)</th><th>Diferencia (CJ / PZ)</th><th>Coincidencia</th><th>Existencia (CJ / PZ)</th><th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((r, i) => (
                          <tr key={i}>
                            <td>
                              {esRefresco(r.categoria) && r.coincidencia.etiqueta !== 'exacto' && (
                                <span role="img" aria-label="Refresco con incidencia" title="Refresco con incidencia">⚠️</span>
                              )} {r.categoria}
                            </td><td className="font-mono">{r.ean}</td><td className="desc-cell">{r.descripcion}</td><td>{r.factor}</td><td className="bold font-mono">{formatoCajasPiezas(r.pedido, r.factor)}</td><td className="bold font-mono">{formatoCajasPiezas(r.enviado, r.factor)}</td><td className={`font-mono ${r.diff > 0 ? 'text-danger' : ''}`}>{formatoCajasPiezas(r.diff, r.factor)}</td><td><span className={`badge ${r.coincidencia.etiqueta}`}>{r.coincidencia.texto}</span></td><td className="font-mono">{formatoCajasPiezas(r.existencia, r.factor)}</td><td><span className={`badge ${r.etiqueta}`}>{r.estado}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* MODAL DE VISTA PREVIA */}
      {previewData && (
        <div className="modal-overlay" onClick={() => setPreviewData(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, color: '#0f7e94' }}>📄 Vista Previa: {previewTitle}</h3>
                <small>{previewData.length} registros</small>
              </div>
              <div className="modal-header-actions">
                <button className="btn-premium-add" onClick={() => { handleSelectFile(previewTitle); setPreviewData(null); }}>
                  ➕ Usar para Comparación
                </button>
                <button className="close-modal-btn" onClick={() => setPreviewData(null)}>&times;</button>
              </div>
            </div>
            <div className="modal-body">
              {loadingPreview ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Cargando vista previa...</p>
                </div>
              ) : (
                <table className="excel-table">
                  <thead>
                    <tr>
                      {previewData[0] && previewData[0].map((header, i) => <th key={i}>{header || `Col ${i + 1}`}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(1).filter(row => row.some(cell => cell !== "" && cell !== null)).map((row, i) => (
                      <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
